import {
  coverPatchToWire,
  mapProjectDto,
  type Project,
  type ProjectDto,
  type UploadedListDto,
} from '@/types';

import type {
  ProjectCreateRequest,
  ProjectListOptions,
  ProjectsService,
  ProjectUpdateRequest,
} from '../projectsService';
import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { buildCoverFormData, COVER_PATH_SUFFIX } from './httpCoverUpload';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`ProjectsService`
 * -----------------------------------------------------------------------------
 * 5 endpoints；純 CRUD。沿用 ``httpRequestWithRefresh``（cookie + CSRF + 401
 * refresh chain）。
 */

const PATH = {
  list: '/api/projects',
  create: '/api/projects',
  item: (id: string) => `/api/projects/${encodeURIComponent(id)}`,
  cover: (id: string) => `/api/projects/${encodeURIComponent(id)}${COVER_PATH_SUFFIX}`,
} as const;

/**
 * T12(docs/50)— list 排序 query。只在 caller 明確帶值時 append(不帶 → BE 走預設
 * `updated_at`/`desc`),避免空 query string;`URLSearchParams` 自動 encode。
 */
function buildListPath(opts?: ProjectListOptions): string {
  if (!opts?.sortBy && !opts?.order) return PATH.list;
  const qs = new URLSearchParams();
  if (opts.sortBy) qs.set('sort_by', opts.sortBy);
  if (opts.order) qs.set('order', opts.order);
  return `${PATH.list}?${qs.toString()}`;
}

function buildCreateBody(req: ProjectCreateRequest): Record<string, unknown> {
  const body: Record<string, unknown> = { name: req.name };
  if (req.description !== undefined) body.description = req.description;
  if (req.brandKitId !== undefined) body.brand_kit_id = req.brandKitId;
  if (req.defaultSettings !== undefined) {
    const ds = req.defaultSettings;
    const out: Record<string, unknown> = {};
    if (ds.aspectRatios !== undefined) out.aspect_ratios = ds.aspectRatios;
    if (ds.languages !== undefined) out.languages = ds.languages;
    if (ds.defaultTemplateId !== undefined) out.default_template_id = ds.defaultTemplateId;
    body.default_settings = out;
  }
  // R245 — 封面。`coverPatchToWire` 保證 `none` 時不夾帶 `color: null`
  // (BE model 是 extra="forbid",多帶欄位 = 422)。
  if (req.cover !== undefined) body.cover = coverPatchToWire(req.cover);
  return body;
}

function buildUpdateBody(patch: ProjectUpdateRequest): Record<string, unknown> {
  // R292.5(Q-15)— brand_kit_id:undefined 不送(=不改);'' = 清除;非空 = 換引用。
  // ⚠️ 千萬不要把「清除」送成 null —— BE 語意 null=不改,清不掉且零錯誤徵兆。
  const body: Record<string, unknown> = {};
  if (patch.name !== undefined) body.name = patch.name;
  if (patch.description !== undefined) body.description = patch.description;
  if (patch.defaultSettings !== undefined) {
    const ds = patch.defaultSettings;
    const out: Record<string, unknown> = {};
    if (ds.aspectRatios !== undefined) out.aspect_ratios = ds.aspectRatios;
    if (ds.languages !== undefined) out.languages = ds.languages;
    if (ds.defaultTemplateId !== undefined) out.default_template_id = ds.defaultTemplateId;
    body.default_settings = out;
  }
  if (patch.cover !== undefined) body.cover = coverPatchToWire(patch.cover);
  if (patch.brandKitId !== undefined) body.brand_kit_id = patch.brandKitId;
  return body;
}

export function createHttpProjectsService(config: HttpClientConfig): ProjectsService {
  return {
    async create(req: ProjectCreateRequest): Promise<Project> {
      const env = await httpRequestWithRefresh<ProjectDto>(
        config,
        'POST',
        PATH.create,
        buildCreateBody(req),
      );
      return mapProjectDto(unwrapResult(env, 'projects.create'));
    },

    async list(opts?: ProjectListOptions): Promise<Project[]> {
      const env = await httpRequestWithRefresh<UploadedListDto<ProjectDto>>(
        config,
        'GET',
        buildListPath(opts),
      );
      const items = env.result?.items ?? [];
      return items.map(mapProjectDto);
    },

    async get(projectId: string): Promise<Project> {
      // Round 50 — 砍掉 `?include_deleted=true` opt-in。admin / audit 需要看
      // 軟刪除資源走獨立 admin service(未來開),使用者面 service 不暴露 admin scope。
      const env = await httpRequestWithRefresh<ProjectDto>(
        config,
        'GET',
        PATH.item(projectId),
      );
      return mapProjectDto(unwrapResult(env, 'projects.get'));
    },

    async update(projectId: string, patch: ProjectUpdateRequest): Promise<Project> {
      const env = await httpRequestWithRefresh<ProjectDto>(
        config,
        'PATCH',
        PATH.item(projectId),
        buildUpdateBody(patch),
      );
      return mapProjectDto(unwrapResult(env, 'projects.update'));
    },

    async remove(projectId: string): Promise<Project> {
      const env = await httpRequestWithRefresh<ProjectDto>(
        config,
        'DELETE',
        PATH.item(projectId),
      );
      return mapProjectDto(unwrapResult(env, 'projects.remove'));
    },

    async setCoverImage(projectId: string, file: File): Promise<Project> {
      // multipart:body 傳 FormData,`httpRequest` 偵測到就不設 Content-Type
      // (boundary 只有瀏覽器知道)。CSRF / X-Current-Group 照常自動帶。
      const env = await httpRequestWithRefresh<ProjectDto>(
        config,
        'POST',
        PATH.cover(projectId),
        buildCoverFormData(file),
      );
      // 回應就是完整 project(cover.kind 已是 image)→ 不用再 GET 一次。
      return mapProjectDto(unwrapResult(env, 'projects.setCoverImage'));
    },
  };
}
